import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, TextField, MenuItem, Alert, CircularProgress, ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

const BASIS = 'Estimated partner surcharge based on approved surcharged transactions — not an official settlement statement.';

// Platform A = EFX/SFTP-settled terminals; Platform B = PAI (GH/NH/HG lanes — TFI settles). The API
// tags every row with its platform; filtering is client-side so the toggle is instant.
const PLATFORM_LABEL = { A: 'Platform A', B: 'Platform B' };
const sumRows = rows => rows.reduce((t, r) => ({ surchargedTxns: t.surchargedTxns + r.surchargedTxns, owed: +(t.owed + r.owed).toFixed(2) }), { surchargedTxns: 0, owed: 0 });

const FALLBACK_PARTNER_GROUPS = ['Blume Holdings LLC', 'Flowhub'];

export default function PartnerSurcharge() {
  const { apiCall } = useApi();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const myGroups = userProfile?.partnerGroups || [];
  const [group, setGroup] = useState(myGroups[0] || '');
  const [partnerGroupOptions, setPartnerGroupOptions] = useState(FALLBACK_PARTNER_GROUPS);
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [platform, setPlatform] = useState('ALL');

  const visibleRows = useMemo(() => {
    const rows = data?.rows || [];
    return platform === 'ALL' ? rows : rows.filter(r => r.platform === platform);
  }, [data, platform]);
  const visibleTotals = useMemo(() => sumRows(visibleRows), [visibleRows]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const loadPartnerGroups = async () => {
      try {
        const resp = await apiCall('/api/v1/channels/partner-groups', 'GET');
        const groups = (resp?.partnerGroups || [])
          .filter(g => g.isActive !== false)
          .map(g => g.groupName)
          .filter(Boolean);
        if (!cancelled && groups.length > 0) {
          setPartnerGroupOptions(groups);
        }
      } catch (e) {
        // Fall back to hardcoded options on failure
      }
    };
    loadPartnerGroups();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const run = async () => {
    if (!start || !end) { setError('Please choose a start and end date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const params = { view: 'partnerSurcharge', startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') };
      if (isAdmin && group) params.group = group;
      const resp = await apiCall('/api/v1/businesses', 'GET', null, params);
      setData(resp);
    } catch (e) { setError(e?.message || 'Failed to load surcharge data'); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!visibleRows.length) return;
    const header = ['Terminal','Location','Platform','Split','Surcharged Txns','Owed'];
    const groups = {};
    visibleRows.forEach(r => { (groups[r.location] = groups[r.location] || []).push(r); });
    const locNames = Object.keys(groups).sort((a,b) => a.localeCompare(b));
    const lines = [];
    locNames.forEach(loc => {
      const gr = groups[loc];
      gr.forEach(r => {
        lines.push([r.terminalId, `"${(r.location||'').replace(/"/g,'""')}"`, PLATFORM_LABEL[r.platform] || '', r.split.toFixed(2), r.surchargedTxns, r.owed.toFixed(2)].join(','));
      });
      const subTxns = gr.reduce((s,r) => s + r.surchargedTxns, 0);
      const subOwed = gr.reduce((s,r) => s + r.owed, 0);
      lines.push([`"${loc} subtotal"`,'','','',subTxns, subOwed.toFixed(2)].join(','));
    });
    lines.push(['TOTAL','','','',visibleTotals.surchargedTxns, visibleTotals.owed.toFixed(2)].join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    const suffix = platform === 'ALL' ? '' : `_platform-${platform}`;
    a.download = `partner-surcharge_${data.group}_${data.startDate}_${data.endDate}${suffix}.csv`; a.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Partner Surcharge</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{data?.basisNote || BASIS}</Alert>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {isAdmin ? (
          <TextField select label="Partner Group" value={group} onChange={e => setGroup(e.target.value)} sx={{ minWidth: 220 }}>
            {partnerGroupOptions.map(g => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            select
            label="Partner Group"
            value={userProfile?.partnerGroups?.[0] || ''}
            disabled
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={userProfile?.partnerGroups?.[0] || ''}>
              {userProfile?.partnerGroups?.[0] || ''}
            </MenuItem>
          </TextField>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker label="Start" value={start} onChange={setStart} />
          <DatePicker label="End" value={end} onChange={setEnd} />
        </LocalizationProvider>
        <ToggleButtonGroup size="small" exclusive value={platform} onChange={(e, v) => { if (v) setPlatform(v); }} aria-label="Platform">
          <ToggleButton value="ALL">All</ToggleButton>
          <ToggleButton value="A">Platform A</ToggleButton>
          <ToggleButton value="B">Platform B</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" onClick={run} disabled={loading || (isAdmin && !group)}>Run</Button>
        <Button variant="outlined" onClick={exportCsv} disabled={!visibleRows.length}>Export CSV</Button>
      </Paper>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {data && !loading && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {data.group} — {data.startDate} to {data.endDate}{platform !== 'ALL' ? ` · ${PLATFORM_LABEL[platform]}` : ''} · {visibleRows.length} terminals · {visibleTotals.surchargedTxns.toLocaleString()} surcharged txns · Total owed ${visibleTotals.owed.toLocaleString(undefined,{minimumFractionDigits:2})}
          </Typography>
          {data.platformTotals && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {['A', 'B'].map(p => (
                <Chip key={p} size="small" variant={platform === p ? 'filled' : 'outlined'} onClick={() => setPlatform(platform === p ? 'ALL' : p)}
                  label={`${PLATFORM_LABEL[p]}: ${data.platformTotals[p].terminals} terminals · ${data.platformTotals[p].surchargedTxns.toLocaleString()} txns · $${data.platformTotals[p].owed.toLocaleString(undefined,{minimumFractionDigits:2})}`} />
              ))}
            </Box>
          )}
          {visibleRows.length === 0 && <Alert severity="warning" sx={{ mb: 1 }}>No {PLATFORM_LABEL[platform] || ''} terminals for this partner in the selected range.</Alert>}
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Terminal</TableCell><TableCell>Location</TableCell><TableCell>Platform</TableCell>
              <TableCell align="right">Split</TableCell><TableCell align="right">Surcharged Txns</TableCell><TableCell align="right">Owed</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(() => {
                const groups = {};
                visibleRows.forEach(r => { (groups[r.location] = groups[r.location] || []).push(r); });
                const locNames = Object.keys(groups).sort((a,b) => a.localeCompare(b));
                return locNames.map(loc => {
                  const gr = groups[loc];
                  const subTxns = gr.reduce((s,r) => s + r.surchargedTxns, 0);
                  const subOwed = gr.reduce((s,r) => s + r.owed, 0);
                  return (
                    <React.Fragment key={loc}>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>{loc} — {gr.length} terminals</TableCell>
                      </TableRow>
                      {gr.map(r => (
                        <TableRow key={`${r.terminalId}-${r.platform}`}>
                          <TableCell sx={{ pl: 4 }}>{r.terminalId}</TableCell>
                          <TableCell>{r.location}</TableCell>
                          <TableCell>{PLATFORM_LABEL[r.platform] || ''}</TableCell>
                          <TableCell align="right">${r.split.toFixed(2)}</TableCell>
                          <TableCell align="right">{r.surchargedTxns.toLocaleString()}</TableCell>
                          <TableCell align="right">${r.owed.toLocaleString(undefined,{minimumFractionDigits:2})}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ fontStyle: 'italic' }}>{loc} subtotal</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{subTxns.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${subOwed.toLocaleString(undefined,{minimumFractionDigits:2})}</TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                });
              })()}
              <TableRow>
                <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>{platform === 'ALL' ? 'GRAND TOTAL' : `${PLATFORM_LABEL[platform]} TOTAL`}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{visibleTotals.surchargedTxns.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${visibleTotals.owed.toLocaleString(undefined,{minimumFractionDigits:2})}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
